import { useEffect, useLayoutEffect } from 'react';
import {
  generatePath,
  useHistory,
  useParams,
  useRouteMatch
} from 'react-router-dom';

import { FormattedMessage, useIntl } from 'react-intl';
import {
  ROUTE_EXERCISES_COLLECTIONS_ENTITY,
  ROUTE_EXERCISES_COLLECTIONS_ENTITY_COLLECTION_DETAILS,
  ROUTE_EXERCISES_COLLECTIONS_ENTITY_COLLECTION_DETAILS_EDIT,
  ROUTE_EXERCISES_COLLECTIONS_ENTITY_COLLECTION_EXERCISES
} from 'routes/RouteList';
import messages from 'messages';

import { TabList, TabPanel, Tabs } from 'components/tabs';
import useTabIndex from 'hooks/utils/useTabIndex';
import Loader from 'components/loader/Loader';
import RetryPanel from 'containers/partials/error-boundary/RetryPanel';

import { useAbility } from '@casl/react';
import { AbilityContext } from 'Can';
import TabPanelBody from 'components/tabs/TabPanelBody';

import { useStore } from 'stores/Store';
import TabLink from 'components/tabs/TabLink';
import CollectionExercisesTab from 'containers/pages/exercises/collections/CollectionExercisesTab';
import CollectionDetailsTab from 'containers/pages/exercises/collections/CollectionDetailsTab';
import BreadcrumbPath from 'containers/partials/misc/BreadcrumbPath';
import useCollection from 'hooks/queries/useCollection';
import ContextMenuWrapper from 'components/menu/ContextMenuWrapper';
import ContextMenu from 'components/menu/ContextMenu';
import {
  ActionButton,
  Panel,
  PanelBody,
  PanelSubtitle,
  PanelTitle,
  PanelTitleButton,
  PanelTitleButtonsGroup,
  PanelTitleWrapper,
  RefactoredPanelHeader
} from 'components/panel';

const Collection = () => {
  const { entityId, tab, templateId, exerciseId } = useParams();
  const { push } = useHistory();
  const ability = useAbility(AbilityContext);
  const intl = useIntl();

  const [tabIndex, setTabIndex] = useTabIndex([
    ROUTE_EXERCISES_COLLECTIONS_ENTITY_COLLECTION_EXERCISES,
    [
      ROUTE_EXERCISES_COLLECTIONS_ENTITY_COLLECTION_DETAILS,
      ROUTE_EXERCISES_COLLECTIONS_ENTITY_COLLECTION_DETAILS_EDIT
    ]
  ]);
  const active = useRouteMatch([
    ROUTE_EXERCISES_COLLECTIONS_ENTITY_COLLECTION_EXERCISES,
    ROUTE_EXERCISES_COLLECTIONS_ENTITY_COLLECTION_DETAILS,
    ROUTE_EXERCISES_COLLECTIONS_ENTITY_COLLECTION_DETAILS_EDIT
  ]);

  const isExercisesTab = useRouteMatch({
    path: ROUTE_EXERCISES_COLLECTIONS_ENTITY_COLLECTION_EXERCISES,
    strict: true,
    exact: true
  });
  const isDetailsTab = useRouteMatch({
    path: [
      ROUTE_EXERCISES_COLLECTIONS_ENTITY_COLLECTION_DETAILS,
      ROUTE_EXERCISES_COLLECTIONS_ENTITY_COLLECTION_DETAILS_EDIT
    ],
    strict: true,
    exact: true
  });

  const { fetchCollection, loading, error } = useCollection();
  const collection = useStore(state => state.collection);
  const crumbs = [
    {
      path: ROUTE_EXERCISES_COLLECTIONS_ENTITY,
      label: intl.formatMessage(messages.breadcrumbCollections)
    }
  ];

  useEffect(() => {
    if (templateId) {
      fetchCollection({ variables: { templateId } });
    }
  }, [templateId]);

  useLayoutEffect(() => {
    // Redirect to the exercises tab if the user is on the training root url
    if (!isExercisesTab && !isDetailsTab && !exerciseId) {
      push(
        generatePath(ROUTE_EXERCISES_COLLECTIONS_ENTITY_COLLECTION_EXERCISES, {
          entityId,
          tab,
          templateId
        })
      );
    }
  }, []);

  if (loading || !collection)
    return (
      <Panel active={active?.isExact}>
        <Loader />
      </Panel>
    );
  if (error) return <RetryPanel />;

  return (
    <>
      <Panel active={active?.isExact}>
        <RefactoredPanelHeader>
          <PanelTitleWrapper>
            {crumbs && <BreadcrumbPath crumbs={crumbs} />}
            <PanelTitle>{collection.title}</PanelTitle>
            <PanelSubtitle>
              <FormattedMessage
                {...messages.exercisesLengthInIt}
                values={{ this: collection.exercises.length }}
              />
            </PanelSubtitle>
          </PanelTitleWrapper>
          <PanelTitleButtonsGroup>
            <ActionButton />
            <ContextMenuWrapper trigger={<PanelTitleButton />}>
              <ContextMenu />
            </ContextMenuWrapper>
          </PanelTitleButtonsGroup>
        </RefactoredPanelHeader>
        <PanelBody>
          <Tabs selectedIndex={tabIndex} onSelect={index => setTabIndex(index)}>
            <TabList>
              <TabLink
                to={generatePath(
                  ROUTE_EXERCISES_COLLECTIONS_ENTITY_COLLECTION_EXERCISES,
                  {
                    entityId,
                    tab,
                    templateId
                  }
                )}
              >
                <FormattedMessage {...messages.collectionsExercises} />
              </TabLink>
              {ability.can('update', collection) && (
                <TabLink
                  to={generatePath(
                    ROUTE_EXERCISES_COLLECTIONS_ENTITY_COLLECTION_DETAILS,
                    {
                      entityId,
                      tab,
                      templateId
                    }
                  )}
                >
                  <FormattedMessage {...messages.collectionsDetails} />
                </TabLink>
              )}
            </TabList>
            <TabPanel>
              <TabPanelBody
                withHeader
                style={{
                  overflow: 'visible',
                  padding: 0,
                  margin: '0 -16px',
                  maxHeight: '100%'
                }}
              >
                <CollectionExercisesTab />
              </TabPanelBody>
            </TabPanel>
            {ability.can('update', collection) && (
              <TabPanel>
                <TabPanelBody>
                  <CollectionDetailsTab />
                </TabPanelBody>
              </TabPanel>
            )}
          </Tabs>
        </PanelBody>
      </Panel>
    </>
  );
};

export default Collection;
